import { easeInOutCubic } from './easing'

/**
 * Scroll to target
 */
export function scrollTo(target: number | string | HTMLElement) {
  if (globalThis.lenis) {
    // Default offset : compensate header & admin bar
    let offset = -(globalThis.$store?.ui.scrollPaddingTop ?? 0)

    // If target is a DOM element, try to read its scroll-margin-top (Tailwind `scroll-mt-*`)
    if (target instanceof HTMLElement) {
      const style = globalThis.getComputedStyle(target)
      const scrollMarginTop = Number.parseFloat(style.scrollMarginTop || '0')

      if (scrollMarginTop > 0) {
        // When a scroll-margin is defined we rely solely on it (browser default behaviour)
        // This mirrors the native anchor scrolling, ensuring consistency with links not handled by Lenis.
        offset = -scrollMarginTop
      }
    }

    globalThis.lenis.scrollTo(target, {
      offset,
      easing: easeInOutCubic,
    })
  } else {
    if (typeof target === 'number') {
      window.scrollTo({ top: target, behavior: 'smooth' })
    } else if (typeof target === 'string') {
      const element = document.querySelector(target)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    } else {
      target.scrollIntoView({ behavior: 'smooth' })
    }
  }
}

export interface ScrollToAnchorOptions {
  pushState?: boolean // Push state to history
  blacklist?: string[] // Blacklist of anchors (e.g. ['#', '#close'])
}

/**
 * Scroll to anchor
 */
export function scrollToAnchor(
  element: Element,
  event?: MouseEvent,
  options?: ScrollToAnchorOptions,
) {
  const pushState = options?.pushState ?? true
  const blacklist = ['#', ...(options?.blacklist ?? [])]

  let hash = element.getAttribute('href')
  if (hash?.startsWith('/en/')) {
    hash = hash.replace('/en/', '')
  }

  if (hash?.startsWith('/')) {
    hash = hash.replace('/', '')
  }

  if (hash?.startsWith('#') && !blacklist.includes(hash)) {
    const element = document.querySelector<HTMLElement>(hash)
    if (element) {
      event?.preventDefault()
      scrollTo(element)
      if (pushState) {
        history.pushState(undefined, '', hash)
      }
    }
  }
}

/**
 * Scroll to the next section
 */
export function scrollToNextSection(element: Element) {
  if (element && element.nextElementSibling) {
    scrollTo(element.nextElementSibling as HTMLElement)
  }
}

export default { scrollTo, scrollToAnchor, scrollToNextSection }
